// COO chat on NomOS's Daimon thread API (/v1/tenants/{t}/daimon/threads). Reshape only.
// Streaming: NomOS emits the same SSE event names and payloads as Sentinel (turn, delta, tool, done), so the
// frontend parser (lib/coo-chat-stream.ts) and CooChatEvent types are unchanged; only transport/errors differ.
// Error codes (thread_not_found, turn_in_progress, too_many_turns, no_turn_running) arrive as
// `error.details.code` and are surfaced as Sentinel's `code`, which lib/coo-tools.ts already words for the UI.
// Files (owner 2026-10-21): uploads go to POST /daimon/files (the blob store; 20 MB) and a message carries their
// `file_ids`; the agent reads them with read_file and hands files back (exports, filled copies) as tool results,
// shown as download chips. Confirmation cards: every write the agent previews is a card on the stored answer
// (`confirmations`); Apply / Dismiss are the person's clicks (/daimon/confirmations/{id}/apply|dismiss). The
// stream's `confirmation` / `file` frames are not forwarded: the page re-reads the stored turn when it ends.
// Gap: no automation-delivery threads (automation_id is always null).
// Grounding: an answer's charts/tables (`blocks`, from the `show` tool), its files (from tool results
// only) and its visible flags come from the stored turn, never from the model's text; a `block`
// event carries a chart the moment `show` accepts it.
import type {
  CooBlockSpec, CooChatEvent, CooConfirmation, CooFile, CooMessage, CooMessageFlag, CooMessageSchedule, CooStopResult,
  CooThread, CooThreadDetail,
} from '@/types';
import { CooChatError, parseSseBuffer } from '@/lib/coo-chat-stream';
import { saveBlob } from '@/lib/download';
import { getConfig, nomosBytes, nomosFetch, toAxiosError, type NomosEnvelope } from '../client';
import type { AnyFn, NomosRegistry } from '../registry';

interface NThread { thread_id: string; title: string; created_at: string; updated_at: string; running: boolean }
interface NMessage {
  message_id: number; seq: number; role: 'user' | 'assistant'; content: string; status: CooMessage['status'];
  error_code: string | null; created_at: string; finished_at: string | null;
  tool_calls?: Array<{ tool: string; ok: boolean; call_id?: string | null; label?: string | null; summary?: string | null;
                       duration_ms?: number | null; detail?: { params?: unknown; preview?: unknown } | null }>;
  proposed_change_ids?: string[];
  blocks?: CooBlockSpec[]; flags?: CooMessageFlag[];
  /** an answer's files carry `id` (grounding), a question's attachments `file_id` */
  files?: Array<{ id?: string; file_id?: string; name: string; content_type?: string; size_bytes?: number }>;
  confirmations?: CooConfirmation[];
  /** a scheduled run's answer: the schedule it came from (owner 29 Oct 2026) */
  schedule?: CooMessageSchedule | null;
}
interface NFile { file_id: string; name: string; content_type: string; size_bytes: number; created_at?: string | null }

const thread = (t: NThread): CooThread => ({
  id: t.thread_id, title: t.title, created_at: t.created_at, updated_at: t.updated_at,
  running: !!t.running, automation_id: null,
});

const message = (m: NMessage): CooMessage => ({
  id: m.message_id, seq: m.seq, role: m.role, content: m.content, status: m.status,
  error_code: m.error_code ?? null, created_at: m.created_at, finished_at: m.finished_at ?? null,
  tool_calls: (m.tool_calls ?? []).map((c) => ({ tool: c.tool, ok: c.ok, call_id: c.call_id ?? null, label: c.label ?? null,
                                                summary: c.summary ?? null, duration_ms: c.duration_ms ?? null,
                                                detail: c.detail ?? null })),
  automation_ids: m.proposed_change_ids ?? [],
  blocks: m.blocks ?? [],
  flags: m.flags ?? [],
  files: (m.files ?? []).map((f): CooFile => ({
    id: (f.id ?? f.file_id) as string, name: f.name, content_type: f.content_type ?? '', size_bytes: f.size_bytes ?? 0,
    source: m.role === 'user' ? 'upload' : 'sentinel', created_at: m.finished_at ?? m.created_at,
  })),
  confirmations: m.confirmations ?? [],
  schedule: m.schedule ?? null,
});

const chatFile = (f: NFile): CooFile => ({
  id: f.file_id, name: f.name, content_type: f.content_type, size_bytes: f.size_bytes, source: 'upload',
  created_at: f.created_at ?? new Date().toISOString(),
});

const FILES = '/daimon/files';
const fileId = (fid: string) => `${FILES}/${encodeURIComponent(fid)}`;

/** Upload one file for a chat message (multipart; NomOS checks name and bytes, at most 20 MB). */
async function uploadCooFile(file: File, signal?: AbortSignal): Promise<CooFile> {
  const cfg = getConfig();
  const url = `${cfg.baseUrl}/v1/tenants/${encodeURIComponent(cfg.tenant)}${FILES}`;
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(url, { method: 'POST', body: form, signal,
                                 headers: { Accept: 'application/json', ...(cfg.token ? { Authorization: `Bearer ${cfg.token}` } : {}) } });
  const text = await res.text();
  let body: unknown = undefined;
  if (text) { try { body = JSON.parse(text); } catch { body = { detail: text }; } }
  if (!res.ok) throw toAxiosError(res.status, body, url);
  return chatFile((body as NomosEnvelope<NFile>).data);
}

/** Save one of the caller's chat files (an upload, or a file a tool made) under its own name. */
async function downloadCooFile(fid: string, name: string): Promise<void> {
  const { blob, filename } = await nomosBytes('GET', `${fileId(fid)}/content`);
  saveBlob(blob, filename || name);
}

/** Remove an upload that never went with a message (a sent file stays with the conversation). */
async function deleteCooFile(fid: string): Promise<void> {
  await nomosFetch('DELETE', fileId(fid));
}

const CARDS = '/daimon/confirmations';

/** The card a decision answered with; a card already decided (or expired) answers its current state. */
async function decide(cid: string, decision: 'apply' | 'dismiss'): Promise<CooConfirmation> {
  try {
    return (await nomosFetch<NomosEnvelope<CooConfirmation>>('POST', `${CARDS}/${encodeURIComponent(cid)}/${decision}`)).data;
  } catch (e) {
    const card = (e as { response?: { data?: { error?: { details?: { card?: CooConfirmation } } } } })
      .response?.data?.error?.details?.card;
    if (card) return card;
    throw e;
  }
}

/** The person applies a card: NomOS replays exactly what was previewed, as them, under their own rights. */
async function applyCooConfirmation(cid: string): Promise<CooConfirmation> {
  return decide(cid, 'apply');
}

async function dismissCooConfirmation(cid: string): Promise<CooConfirmation> {
  return decide(cid, 'dismiss');
}

/** Undo an applied card: NomOS undoes it as the person and records it on the card (status `undone`), step
 *  by step, so a retry after a partial undo continues; a later write by someone else is never undone. */
async function undoCooConfirmation(card: CooConfirmation): Promise<CooConfirmation> {
  try {
    return (await nomosFetch<NomosEnvelope<CooConfirmation>>('POST',
      `${CARDS}/${encodeURIComponent(card.confirmation_id)}/undo`)).data;
  } catch (e) {
    const err = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error;
    throw Object.assign(new Error(err?.message ?? 'The change could not be undone.'), { cause: e });
  }
}

/** The card's current state (a card seen while it is being applied is re-read until it settles). */
async function getCooConfirmation(cid: string): Promise<CooConfirmation> {
  return (await nomosFetch<NomosEnvelope<CooConfirmation>>('GET', `${CARDS}/${encodeURIComponent(cid)}`)).data;
}

const BASE = '/daimon/threads';
const id = (threadId: string) => `${BASE}/${encodeURIComponent(threadId)}`;

async function listCooThreads(): Promise<CooThread[]> {
  const r = await nomosFetch<NomosEnvelope<{ threads: NThread[] }>>('GET', BASE);
  return (r.data?.threads ?? []).map(thread);
}

async function createCooThread(): Promise<CooThread> {
  return thread((await nomosFetch<NomosEnvelope<NThread>>('POST', BASE, {})).data);
}

async function getCooThread(threadId: string): Promise<CooThreadDetail> {
  const d = (await nomosFetch<NomosEnvelope<NThread & { messages: NMessage[] }>>('GET', id(threadId))).data;
  return { ...thread(d), messages: (d.messages ?? []).map(message) };
}

async function deleteCooThread(threadId: string): Promise<void> {
  await nomosFetch('DELETE', id(threadId));
}

async function stopCooChatTurn(threadId: string): Promise<CooStopResult> {
  const d = (await nomosFetch<NomosEnvelope<CooStopResult>>('POST', `${id(threadId)}/stop`)).data;
  return { run_id: d.run_id, stop_requested_at: d.stop_requested_at };
}

/** Pure: map one NomOS SSE frame to the frontend's event (names/payloads already match; kept explicit and strict). */
export function mapNomosEvent(e: CooChatEvent): CooChatEvent | null {
  switch (e.event) {
    case 'turn': case 'delta': case 'tool': case 'block': case 'done': return e;
    default: return null;
  }
}

async function streamCooChatMessage(threadId: string, text: string, onEvent: (e: CooChatEvent) => void,
                                    signal?: AbortSignal, fileIds: string[] = []): Promise<void> {
  const cfg = getConfig();
  const url = `${cfg.baseUrl}/v1/tenants/${encodeURIComponent(cfg.tenant)}${id(threadId)}/messages`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream',
               ...(cfg.token ? { Authorization: `Bearer ${cfg.token}` } : {}) },
    body: JSON.stringify(fileIds.length ? { text, file_ids: fileIds } : { text }),
    signal,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: { message?: string; details?: { code?: string } }; detail?: unknown };
    throw new CooChatError(response.status, body.error?.details?.code ?? 'error',
      typeof body.error?.message === 'string' ? body.error.message : typeof body.detail === 'string' ? body.detail : `HTTP ${response.status}`);
  }
  if (!response.body) throw new CooChatError(0, 'no_stream', 'Streaming is not supported in this browser');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const emit = (events: CooChatEvent[]) => events.forEach((e) => { const m = mapNomosEvent(e); if (m) onEvent(m); });
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = parseSseBuffer(buffer);
      buffer = rest;
      emit(events);
    }
    emit(parseSseBuffer(buffer + decoder.decode() + '\n\n').events);
  } finally {
    reader.releaseLock();
  }
}

export const chatImpl: NomosRegistry = {
  listCooThreads: listCooThreads as unknown as AnyFn,
  createCooThread: createCooThread as unknown as AnyFn,
  getCooThread: getCooThread as unknown as AnyFn,
  deleteCooThread: deleteCooThread as unknown as AnyFn,
  stopCooChatTurn: stopCooChatTurn as unknown as AnyFn,
  streamCooChatMessage: streamCooChatMessage as unknown as AnyFn,
  uploadCooFile: uploadCooFile as unknown as AnyFn,
  downloadCooFile: downloadCooFile as unknown as AnyFn,
  deleteCooFile: deleteCooFile as unknown as AnyFn,
  applyCooConfirmation: applyCooConfirmation as unknown as AnyFn,
  dismissCooConfirmation: dismissCooConfirmation as unknown as AnyFn,
  undoCooConfirmation: undoCooConfirmation as unknown as AnyFn,
  getCooConfirmation: getCooConfirmation as unknown as AnyFn,
};
